<template>
    <div class="n-files-upload" ref="filesUplad">
        <form :id="formId" enctype="multipart/form-data" novalidate>
            <div class="n-files-upload__dropzone js-loading">
                <p v-if="isSaving"><span class="n-files-upload__spinner"></span><slot name="upload-saving-singluar" v-if="fileCount === 1">Uploading {{ fileCount }} file...</slot><slot name="upload-saving-plural" v-else>Uploading {{ fileCount }} files...</slot></p>
                <label :for="inputId" class="n-files-upload__label" v-if="!isSaving"><slot name="upload-label">Drag your file(s) here to upload, or click to browse...</slot></label>
                <input type="file" name="files" :id="inputId" :accept="allowedFileTypes" :multiple="allowMultiple" :disabled="isSaving" :title="$slots['upload-title'] ? $slots['upload-title'][0].text : null" class="n-files-upload__input" ref="filesUploadInput" @change="uploadFiles($event.target)" />
            </div>
        </form>

        <transition name="notify">
            <div class="n-files-upload__status n-files-upload__status--success" v-if="isSuccess">
                <p><slot name="upload-success-singluar" v-if="fileCount === 1">Uploaded {{ fileCount }} file successfully.</slot><slot name="upload-success-plural" v-else>Uploaded {{ fileCount }} files successfully.</slot></p>
            </div>
            <div class="n-files-upload__status n-files-upload__status--failed" v-if="isFailed">
                <p><slot name="upload-failed">Uploaded failed.</slot></p>
                <pre>{{ formattedUploadError }}</pre>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'NFilesUpload',
    props: {
        formId: {
            type: String,
            default: 'formId'
        },
        inputName: {
            type: String,
            default: 'inputName'
        },
        inputId: {
            type: String,
            default: 'inputId'
        },
        allowedFileTypes: {
            type: String,
            default: '*'
        },
        allowMultiple: {
            type: Boolean,
            default: true
        },
        uploadStatus: {
            type: String,
            default: 'initial'
        },
        uploadErrorResponse: {
            type: [String, Object, Error],
            default: null
        }
    },
    data: function () {
        return {
            fileCount: 0,
            currentStatus: null
        }
    },
    computed: {
        isInitial () {
            return this.currentStatus === 'initial'
        },
        isSaving () {
            return this.currentStatus === 'saving'
        },
        isSuccess () {
            return this.currentStatus === 'success'
        },
        isFailed () {
            return this.currentStatus === 'failed'
        },
        formattedUploadError () {
            return this.getUploadErrorMessage(this.uploadErrorResponse)
        }
    },
    watch: {
        uploadStatus: {
            immediate: true,
            handler (value) {
                this.currentStatus = value
            }
        }
    },
    methods: {
        getUploadErrorMessage (error) {
            if (!error) {
                return ''
            }

            if (typeof error === 'string') {
                return error
            }

            const responseData = error.response && error.response.data ? error.response.data : error

            if (typeof responseData === 'string') {
                return responseData
            }

            if (responseData.message) {
                return responseData.message
            }

            if (responseData.Message) {
                return responseData.Message
            }

            if (responseData.error) {
                return responseData.error
            }

            if (responseData.Error) {
                return responseData.Error
            }

            if (responseData.title) {
                return responseData.title
            }

            if (responseData.ExceptionMessage) {
                return responseData.ExceptionMessage
            }

            if (responseData.responseText) {
                return responseData.responseText
            }

            if (error.request && error.request.responseText) {
                return error.request.responseText
            }

            if (responseData.errors && typeof responseData.errors === 'object') {
                return Object.values(responseData.errors)
                    .flat()
                    .join('\n')
            }

            if (responseData.modelState && typeof responseData.modelState === 'object') {
                return Object.values(responseData.modelState)
                    .flat()
                    .join('\n')
            }

            if (error.message) {
                return error.message
            }

            return JSON.stringify(responseData, null, 2)
        },
        resetForm () {
            // Reset form to initial state
            this.fileCount = 0
            this.currentStatus = 'initial'
        },
        uploadFiles (element) {
            if (!element.files.length) {
                return false
            }

            this.fileCount = element.files.length

            // Send the formData being submitted
            this.$emit('uploadFiles', element)
        }
    },
    created: function () {
    },
    mounted: function () {
        this.resetForm()
    }
}
</script>

<style lang="scss">
@import '../../../scss/utils/colors.scss';

.n-files-upload__dropzone {
    align-items: center;
    border: 2px dashed var(--primaryColor);
    display: flex;
    justify-content: center;
    margin-bottom: 2em;
    margin-top: 2em;
    min-height: 144px;
    padding: 1em 1em;
    position: relative;
    transition: background 0.2s var(--ease-in-out-quad);
}
.n-files-upload__dropzone:focus-within {
    background: var(--buttonBackgroundColor);
    outline: 3px solid var(--outlineColor);
    outline-offset: 2px;
}
.n-files-upload__dropzone:hover {
    background: var(--buttonBackgroundColor);
}

.n-files-upload__spinner {
    display: inline-block;
    height: 40px;
    margin-left: 1em;
    margin-right: 1em;
    position: relative;
    vertical-align: middle;
    width: 40px;
}
.n-files-upload__spinner::before,
.n-files-upload__spinner::after {
    animation-name: beaming;
    animation-duration: 1.6s;
    animation-iteration-count: infinite;
    animation-timing-function: var(--ease-out-cubic);
    border: 4px solid $selected-dark-gray;
    border-radius: 50%;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transform: scale(0.01);
    width: 100%;
}
.n-files-upload__spinner::after {
    animation-delay: 0.4s;
}
.n-files-upload__spinner::before,
.n-files-upload__spinner::after {
    animation-name: beaming;
    animation-duration: 1.6s;
    animation-iteration-count: infinite;
    animation-timing-function: var(--ease-out-cubic);
    border: 4px solid $selected-dark-gray;
    border-radius: 50%;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transform: scale(0.01);
    width: 100%;
}
.n-files-upload__spinner::after {
    animation-delay: 0.4s;
}

.n-files-upload__label {
    display: block;
    margin-bottom: 0;
    margin-top: 0;
    text-align: center;
}

.n-files-upload__input {
    cursor: pointer;
    height: calc(100% + 4px);
    left: -2px;
    opacity: 0;
    padding: 0;
    position: absolute;
    top: -2px;
    width: calc(100% + 4px);
}
.n-files-upload__input[disabled] {
    cursor: not-allowed;
}

.n-files-upload__status {
    border: 2px solid grey;
    margin-bottom: 2em;
    margin-top: 2em;
    padding: 1em;

}
.n-files-upload__status--success {
    background: rgba($button-green, 0.1);
    border-color: $button-green;
}
.n-files-upload__status--failed {
    background: rgba($button-red, 0.1);
    border-color: $button-red;
}
.n-files-upload__status p,
.n-files-upload__status pre {
    margin-bottom: 0;
    margin-top: 0;
}

.notify-enter-active {
    animation: fade 0.3s;
}
.notify-leave-active {
    animation: fade 0.3s reverse;
}

@keyframes fade {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

@keyframes beaming {
    0% {
        opacity: 1;
        transform: scale(0.01);
    }
    50% {
        opacity: 1;
        transform: scale(1);
    }
    50%,
    100% {
        opacity: 0;
        transform: scale(1);
    }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/files/files-upload.vue